// Pure helpers for appending transactions and producing a stable id.
// The reducer is the only caller — keeps the ledger append-only.

import type { GameState, Txn, TxnCategory, TxnType } from '../state/types.js';

export type TxnInput = {
  type: TxnType;
  amount: number;
  category: TxnCategory;
  note: string;
};

/**
 * Produce a deterministic txn id from the current ledger length and the action that produced it.
 * Tests rely on these ids being stable across re-runs of the same action log.
 */
export function nextTxnId(state: GameState): string {
  return `txn-${state.ledger.length + 1}`;
}

/** Build a Txn from input + state context (week, id). Does NOT mutate state. */
export function buildTxn(state: GameState, input: TxnInput): Txn {
  return {
    id: nextTxnId(state),
    week: state.clock.week,
    type: input.type,
    amount: input.amount,
    category: input.category,
    note: input.note,
  };
}

/**
 * Apply a single txn: append to ledger and adjust wallet cash.
 * Returns a new state — pure.
 */
export function applyTxn(state: GameState, input: TxnInput): GameState {
  const txn = buildTxn(state, input);
  return {
    ...state,
    wallet: { ...state.wallet, cash: state.wallet.cash + txn.amount },
    ledger: [...state.ledger, txn],
  };
}

/** Apply multiple txns in order — convenient for a settle step. */
export function applyTxns(state: GameState, inputs: readonly TxnInput[]): GameState {
  return inputs.reduce<GameState>(applyTxn, state);
}
